import { Box } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';

import {
  formatInteger,
  formatMoney,
  formatPayrollType,
} from '@octopus/formatters';
import { payrollStatusIndex, payslipStatuses } from '@octopus/payroll-types';

import {
  approvalDateColumn,
  periodColumn,
  reviewActionColumn,
} from '../../../modules/components/table/columns/PayrollColumns';
import {
  countPayrollStatus,
  hasGeneratedAllPayslips,
} from '../../../utils/statusIndexUtils';
import { PeriodGroupsConfigType, TypedPeriodSummary } from '../types';

import { PeriodActionsMenu } from './PeriodActionsColumn';
import { ReceiptColumn } from './ReceiptColumn';
import { SendPayslip } from './SendPayslip';

export const commonColumns: GridColDef<TypedPeriodSummary>[] = [
  periodColumn(),
  {
    field: 'type',
    headerName: 'Tipo de folha',
    renderHeader: () => <Box whiteSpace="nowrap">Tipo de folha</Box>,
    sortable: false,
    valueGetter: (params: GridValueGetterParams) => {
      return formatPayrollType(params.row.type);
    },
  },
  {
    field: 'counts',
    headerName: 'Colaboradores',
    sortable: false,
    valueGetter: (params) => {
      const payrollsCount = {
        open: countPayrollStatus(
          params.row.totalizers,
          (statusIndex) => statusIndex <= payrollStatusIndex.open,
        ),
        approved: countPayrollStatus(
          params.row.totalizers,
          (statusIndex) =>
            statusIndex > payrollStatusIndex.open &&
            statusIndex <= payrollStatusIndex.approved,
        ),
        closed: countPayrollStatus(
          params.row.totalizers,
          (statusIndex) =>
            statusIndex > payrollStatusIndex.approved &&
            statusIndex < payrollStatusIndex.archived,
        ),
        archived: countPayrollStatus(
          params.row.totalizers,
          (statusIndex) => statusIndex === payrollStatusIndex.archived,
        ),
      }[params.row.status];

      return formatInteger(payrollsCount, {
        minimumIntegerDigits: 2,
      });
    },
  },
  {
    field: 'netPay',
    headerName: 'Valor líquido',
    sortable: false,
    valueGetter: (params) =>
      formatMoney(params.row.totalizers.totals.netPay) || '--',
  },
  {
    field: 'totalCost',
    headerName: 'Custo total',
    sortable: false,
    valueGetter: (params) =>
      formatMoney(params.row.totalizers.totals.totalCost) || '--',
  },
];

export function getColumnsForStatus(state: PeriodGroupsConfigType) {
  switch (state) {
    case 'open':
      return [...commonColumns, approvalDateColumn(), reviewActionColumn()];
    case 'approved':
      return [
        ...commonColumns,
        payrollPeriodReceiptsColumn(),
        sendUntilColumn(),
        actionsColumn(),
      ];
    case 'closed':
      return [...commonColumns, payrollPeriodReceiptsColumn(), actionsColumn()];
    default:
      return commonColumns;
  }
}

export const payrollPeriodReceiptsColumn = (): GridColDef => ({
  field: 'receipts',
  headerName: 'Recibos',
  renderCell: (params: GridRenderCellParams) => {
    const hasAlreadySentAll =
      params.row?.totalizers?.counts?.payslips?.byStatus?.[
        payslipStatuses.sent
      ] > 0;
    const isScheduled = !!params.row.payslipScheduledSendAt;
    const isGeneratingPayslips = !hasGeneratedAllPayslips(
      params.row.totalizers,
    );
    return (
      <ReceiptColumn
        isPeriod
        row={params.row}
        hasSent={hasAlreadySentAll}
        isScheduled={isScheduled}
        isGeneratingPayslips={isGeneratingPayslips}
      />
    );
  },
});

export const payrollRPAReceiptsColumn = (): GridColDef => ({
  field: 'receipts',
  headerName: 'RPA',
  renderCell: (params: GridRenderCellParams) => {
    const hasAlreadySent = !params.row.hasApprovedPayslip;
    const isScheduled = !!params.row.payslipScheduledSendAt;
    return (
      <ReceiptColumn
        isPeriod={false}
        row={params.row}
        hasSent={hasAlreadySent}
        isScheduled={isScheduled}
        isGeneratingPayslips={false}
      />
    );
  },
});

export const sendUntilColumn = (): GridColDef => ({
  field: 'sendUntil',
  headerName: 'Enviar até',
  renderCell: (params: GridRenderCellParams) => {
    return <SendPayslip row={params.row} />;
  },
});

export const actionsColumn = (): GridColDef => ({
  field: 'actions',
  headerName: '',
  renderCell: (params: GridRenderCellParams) => {
    return <PeriodActionsMenu row={params.row} />;
  },
});
